import type { Permission, PersonaType, RoleId, ScopedAssignment, SessionUser } from '../types/domain'
import { getActiveSchoolUnitId } from './scope'
import { ApiError } from './apiError'
import { fetchWithTimeout } from './http'

export { DEFAULT_REQUEST_TIMEOUT_MS, LONG_REQUEST_TIMEOUT_MS, REQUEST_TIMEOUT_CODE } from './http'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

export interface BackendAuthUser {
  id: string
  email: string
  fullName: string
  mustChangePassword?: boolean
  assignments?: Array<{
    assignmentId: string
    roleId: string
    roleCode: string
    scopeType: 'GLOBAL' | 'FOUNDATION' | 'SCHOOL_UNIT'
    foundationId: string | null
    schoolUnitId: string | null
    permissions: string[]
  }>
}

export interface BackendLoginResponse {
  accessToken: string
  expiresIn: string
  user: BackendAuthUser
}

export function roleCodeToRoleId(roleCode: string): RoleId {
  if (roleCode === 'SYSTEM_ADMIN') return 'super_admin'
  if (roleCode === 'SPMB_ADMIN') return 'admin_spmb'
  if (roleCode === 'SPMB_OBSERVER') return 'observer_spmb'
  if (roleCode === 'FINANCE_OFFICER') return 'admin_finance'
  if (roleCode === 'SPMB_PARENT') return 'parent'
  if (roleCode === 'SPMB_LEADERSHIP') return 'pimpinan'
  if (roleCode === 'ACCESS_ADMIN') return 'admin_hr'
  if (roleCode.startsWith('REFERRAL')) return 'referral'
  return 'unknown'
}

export function mapBackendUserToSession(data: BackendLoginResponse): SessionUser {
  const assignments: ScopedAssignment[] = (data.user.assignments ?? []).map((assignment) => ({
    ...assignment,
    permissions: assignment.permissions as Permission[],
  }))

  const hasParentAssignment = assignments.some(
    (a) => a.roleCode === 'SPMB_PARENT' || a.roleCode === 'parent',
  )
  const hasStaffAssignment = assignments.some(
    (a) =>
      a.roleCode !== 'SPMB_PARENT' &&
      a.roleCode !== 'parent' &&
      !a.roleCode.toUpperCase().startsWith('REFERRAL'),
  )

  const availablePersonas: PersonaType[] = []
  if (hasStaffAssignment) availablePersonas.push('STAFF')
  if (hasParentAssignment) availablePersonas.push('PARENT')
  if (availablePersonas.length === 0) {
    const primaryCode = assignments[0]?.roleCode ?? ''
    availablePersonas.push(primaryCode === 'SPMB_PARENT' || primaryCode === 'parent' ? 'PARENT' : 'STAFF')
  }

  let savedPersona: PersonaType | null = null
  try {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('wicend.erp.activePersona')
      if (stored === 'STAFF' || stored === 'PARENT') {
        savedPersona = stored
      }
    }
  } catch {
    // Ignore storage errors
  }

  const activePersona: PersonaType =
    savedPersona && availablePersonas.includes(savedPersona)
      ? savedPersona
      : (availablePersonas[0] ?? 'STAFF')

  const primaryStaffAssignment = assignments.find(
    (a) =>
      a.roleCode !== 'SPMB_PARENT' &&
      a.roleCode !== 'parent' &&
      !a.roleCode.toUpperCase().startsWith('REFERRAL'),
  )
  const primaryAssignment =
    activePersona === 'PARENT'
      ? assignments.find((a) => a.roleCode === 'SPMB_PARENT' || a.roleCode === 'parent')
      : primaryStaffAssignment ?? assignments[0]

  const roleCode = primaryAssignment?.roleCode ?? 'unknown'
  const role: RoleId = roleCodeToRoleId(roleCode)

  const backendPermissions = new Set<string>()
  data.user.assignments?.forEach((assignment) => {
    assignment.permissions?.forEach((perm) => backendPermissions.add(perm))
  })

  const permissions = Array.from(backendPermissions) as Permission[]

  return {
    id: data.user.id,
    name: data.user.fullName,
    email: data.user.email,
    role,
    permissions,
    assignments,
    accessToken: data.accessToken,
    activePersona,
    availablePersonas,
    mustChangePassword: data.user.mustChangePassword ?? false,
  }
}

export async function loginWithApi(email: string, password: string): Promise<SessionUser> {
  const response = await fetchWithTimeout(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  })

  const payload: unknown = await response.json().catch(() => ({}))

  // Login runs before any session exists, so it cannot go through
  // `fetchWithAuth()`. The error shape is still the shared one.
  if (!response.ok) {
    throw toApiError(
      response,
      payload,
      'Login ke backend gagal. Periksa kembali email dan password.',
    )
  }

  return mapBackendUserToSession(unwrapEnvelope<BackendLoginResponse>(payload))
}

export async function requestPasswordResetOtp(
  email: string,
): Promise<{ success: boolean; maskedPhone: string }> {
  const response = await fetchWithTimeout(`${API_BASE_URL}/auth/forgot-password/request`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  })

  const payload: unknown = await response.json().catch(() => ({}))

  if (!response.ok) {
    throw toApiError(
      response,
      payload,
      'Gagal meminta kode verifikasi WhatsApp. Pastikan email Anda sudah benar.',
    )
  }

  return unwrapEnvelope<{ success: boolean; maskedPhone: string }>(payload)
}

export async function resetPasswordWithOtp(data: {
  email: string
  otp: string
  newPassword: string
}): Promise<{ success: boolean; message: string }> {
  const response = await fetchWithTimeout(`${API_BASE_URL}/auth/forgot-password/reset`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })

  const payload: unknown = await response.json().catch(() => ({}))

  if (!response.ok) {
    throw toApiError(
      response,
      payload,
      'Gagal mengatur ulang kata sandi. Pastikan kode OTP sesuai.',
    )
  }

  return unwrapEnvelope<{ success: boolean; message: string }>(payload)
}

export function isTokenExpired(token?: string | null): boolean {
  if (!token) return true
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return false
    const base64Url = parts[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    )
    const payload = JSON.parse(jsonPayload) as { exp?: number }
    if (payload && typeof payload.exp === 'number') {
      // payload.exp is in seconds
      return Date.now() >= payload.exp * 1000
    }
  } catch {
    return false
  }
  return false
}

export function handleSessionExpired(reason = 'Token is invalid or expired'): void {
  try {
    localStorage.removeItem('wicend.erp.session')
    localStorage.removeItem('token')
  } catch (err) {
    console.error('Failed to clear session from localStorage', err)
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('wicend:auth-expired', { detail: { reason } }))

    const pathname = window.location.pathname
    if (!pathname.startsWith('/login')) {
      const url = new URL('/login', window.location.origin)
      url.searchParams.set('expired', '1')
      if (pathname && pathname !== '/' && pathname.startsWith('/app')) {
        url.searchParams.set('from', pathname)
      }
      window.location.href = url.pathname + url.search
    }
  }
}

export async function fetchWithAuth(
  endpoint: string,
  options: RequestInit = {},
  /** Defaults to 30 s, or 120 s for a multipart body. */
  timeoutMs?: number,
): Promise<Response> {
  const sessionRaw = localStorage.getItem('wicend.erp.session')
  let session: SessionUser | null = null
  try {
    session = sessionRaw ? (JSON.parse(sessionRaw) as SessionUser) : null
  } catch (err) {
    console.error('Failed to parse session', err)
    localStorage.removeItem('wicend.erp.session')
  }

  // If token is already expired client-side, clear session and redirect immediately
  if (session?.accessToken && isTokenExpired(session.accessToken)) {
    handleSessionExpired('Token is invalid or expired')
    return new Response(
      JSON.stringify({
        statusCode: 401,
        message: 'Token is invalid or expired',
        error: 'Unauthorized',
      }),
      {
        status: 401,
        statusText: 'Unauthorized',
        headers: { 'Content-Type': 'application/json' },
      },
    )
  }

  const headers = new Headers(options.headers || {})
  if (options.body && !headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }
  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }

  // Narrows results to the unit picked in the header. The backend intersects it
  // with the caller's own scope, so this can only ever restrict, never widen.
  // We do NOT send this header when requesting the master list of school units,
  // otherwise the switcher locks itself into only the currently selected unit.
  const activeSchoolUnitId = getActiveSchoolUnitId()
  if (activeSchoolUnitId && !endpoint.includes('/organization/school-units')) {
    headers.set('X-Active-School-Unit', activeSchoolUnitId)
  }

  const response = await fetchWithTimeout(
    `${API_BASE_URL}${endpoint}`,
    { ...options, headers },
    timeoutMs,
  )

  if (response.status === 401) {
    const clone = response.clone()
    clone
      .json()
      .then((body) => {
        const message =
          typeof body?.message === 'string'
            ? body.message
            : Array.isArray(body?.message)
              ? body.message.join(', ')
              : 'Token is invalid or expired'
        handleSessionExpired(message)
      })
      .catch(() => {
        handleSessionExpired('Token is invalid or expired')
      })
  }

  return response
}

interface SuccessEnvelope<T> {
  status: 'OK'
  data: T
  meta?: PageMeta
  requestId?: string
}

interface ErrorEnvelope {
  status: 'ERROR'
  error: {
    code: string
    message: string
    details?: Record<string, unknown>
    correlationId?: string
  }
  requestId?: string
}

export interface PageMeta {
  page: number
  limit: number
  total: number
  totalPages: number
}

export interface Page<T> {
  data: T[]
  meta: PageMeta
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/**
 * Reads one API response and returns its payload.
 *
 * Replaces the `fetchWithAuth()` + `res.ok` + `res.json()` trio repeated across
 * dozens of files, each with its own error handling. Failures always arrive as
 * an `ApiError` carrying a `code`, so callers no longer have to guess whether
 * `message` is a string or an array.
 */
export async function apiJson<T>(
  endpoint: string,
  options: RequestInit = {},
  /**
   * User-facing text to show when the backend supplies no message of its own.
   * Call sites keep the Indonesian copy they already displayed, so migrating a
   * file changes what the user reads in no way at all.
   */
  fallbackMessage?: string,
): Promise<T> {
  const response = await fetchWithAuth(endpoint, options)

  // A 204 or an empty body is not a failure: some endpoints legitimately
  // return nothing.
  const raw = await response.text()
  const payload: unknown = raw.length > 0 ? safeParse(raw) : null

  if (!response.ok) throw toApiError(response, payload, fallbackMessage)

  if (isRecord(payload) && payload.status === 'OK' && 'data' in payload) {
    if ('meta' in payload && payload.meta !== undefined) {
      return {
        data: payload.data,
        meta: payload.meta,
      } as T
    }
    return (payload as unknown as SuccessEnvelope<T>).data
  }

  // @RawResponse() endpoints are never wrapped; hand their body back as is.
  return payload as T
}

/**
 * Like `apiJson()`, but for paginated collections: the envelope lifts `meta` to
 * the top level, so the two are reassembled into one object here.
 */
export async function apiPage<T>(
  endpoint: string,
  options: RequestInit = {},
  fallbackMessage?: string,
): Promise<Page<T>> {
  const response = await fetchWithAuth(endpoint, options)
  const raw = await response.text()
  const payload: unknown = raw.length > 0 ? safeParse(raw) : null

  if (!response.ok) throw toApiError(response, payload, fallbackMessage)

  if (isRecord(payload) && Array.isArray(payload.data)) {
    const meta = isRecord(payload.meta) ? (payload.meta as unknown as PageMeta) : undefined
    return {
      data: payload.data as T[],
      meta: meta ?? { page: 1, limit: payload.data.length, total: payload.data.length, totalPages: 1 },
    }
  }

  throw new ApiError({
    message: 'Bentuk response berhalaman tidak dikenali.',
    code: 'MALFORMED_PAGE_RESPONSE',
    status: response.status,
  })
}

/**
 * Unwraps an envelope read outside `apiJson()` - the handful of public
 * endpoints that run before a session exists and so cannot use it.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function unwrapEnvelope<T = any>(payload: unknown): T {
  if (isRecord(payload) && payload.status === 'OK' && 'data' in payload) {
    if ('meta' in payload && payload.meta !== undefined) {
      return {
        data: payload.data,
        meta: payload.meta,
      } as T
    }
    return (payload as unknown as SuccessEnvelope<T>).data
  }
  return payload as T
}

function safeParse(raw: string): unknown {
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function toApiError(response: Response, payload: unknown, fallbackMessage?: string): ApiError {
  const requestId = isRecord(payload) && typeof payload.requestId === 'string' ? payload.requestId : undefined

  // Envelope shape.
  if (isRecord(payload) && payload.status === 'ERROR' && isRecord(payload.error)) {
    const error = payload.error as ErrorEnvelope['error']
    return new ApiError({
      message: error.message || fallbackMessage || 'Permintaan gagal.',
      code: error.code || `HTTP_${response.status}`,
      status: response.status,
      details: error.details,
      correlationId: error.correlationId,
      requestId,
    })
  }

  // Legacy shape, for @RawResponse() endpoints and for failures produced
  // outside the filter - the synthetic 401 fetchWithAuth() returns when the
  // token has already expired, for instance.
  const legacy = isRecord(payload) ? payload : {}
  return new ApiError({
    message: legacyMessage(legacy) ?? fallbackMessage ?? 'Permintaan gagal.',
    code: typeof legacy.code === 'string' ? legacy.code : `HTTP_${response.status}`,
    status: response.status,
    requestId,
  })
}

function legacyMessage(payload: Record<string, unknown>): string | undefined {
  if (typeof payload.message === 'string') return payload.message
  if (Array.isArray(payload.message)) {
    const parts = payload.message
      .map((item) => (typeof item === 'string' ? item : isRecord(item) && typeof item.message === 'string' ? item.message : null))
      .filter((part): part is string => part !== null)
    return parts.length > 0 ? parts.join(', ') : undefined
  }
  return undefined
}

export async function changeOwnPassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await apiJson(
    '/auth/change-password',
    { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) },
    'Kata sandi gagal diubah.',
  )
}

export async function forceChangeOwnPassword(newPassword: string): Promise<void> {
  await apiJson(
    '/auth/force-change-password',
    { method: 'POST', body: JSON.stringify({ newPassword }) },
    'Kata sandi baru gagal disimpan.',
  )
}

export async function updateOwnProfile(payload: {
  fullName: string
  phone?: string
}): Promise<{ id: string; fullName: string; email: string }> {
  return apiJson(
    '/auth/profile',
    { method: 'PATCH', body: JSON.stringify(payload) },
    'Profil gagal diperbarui.',
  )
}

export interface BackendMenu {
  id: string
  parentId: string | null
  code: string
  name: string
  icon: string | null
  path: string | null
  sortOrder: number
  menuType: 'group' | 'item' | 'divider'
  permissions: string[]
}

export async function fetchMenus(): Promise<BackendMenu[]> {
  // A failure is deliberately soft: the shell falls back to the routes it knows
  // rather than rendering an error where the navigation should be.
  try {
    return await apiJson<BackendMenu[]>('/iam/menus')
  } catch {
    return []
  }
}
